// TODO - protect this route from direct access

import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { find as _find, get as _get, isEmpty as _isEmpty, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Store
import {
  claimFnolQuery,
  DraftClaim,
  fromClaimFnolActions,
  Incident,
  ReportClaimFnolService,
  SelectOptions
} from '@amfam/claim/fnol/data-access';
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { FnolRiskModel, VehicleType } from '@amfam/policy/models';
import { AnalyticsFacade, ClaimsAnalytics } from '@amfam/shared/analytics';
import { ClaimsNavigationPaths } from '@amfam/shared/models';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import { ClaimsFnolFormControls } from '../../shared/claims-constants';

@Component({
  selector: 'ds-report-claim-loss-cause',
  templateUrl: './report-claim-loss-cause.component.html',
  styleUrls: ['../report-claim-fnol.component.scss'],
  standalone: false
})
export class ReportClaimLossCauseComponent implements OnInit, OnDestroy {
  public lossCauseForm: UntypedFormGroup;
  public details: AbstractControl;
  public causeGroup: UntypedFormGroup;
  public lossCause: SelectOptions;
  public riskTypeCar: Boolean = true;
  public riskTypeBoat: Boolean = false;
  public draftClaim: DraftClaim;
  public pageTitleName = 'File a Claim';
  private amfamDotCom = '';
  public lossCauses: Array<SelectOptions>;
  public collisionDetailCauses: Array<SelectOptions>;
  public weatherDetailCauses: Array<SelectOptions>;
  public theftDetailCauses: Array<SelectOptions>;
  private claimNumber: string;
  public unMappedLossCause: string;
  private risks: Array<FnolRiskModel> = new Array<FnolRiskModel>();
  private currentRisk: FnolRiskModel;

  public loading: Observable<Boolean>;

  // Shared Subject for completing all observables on destroy
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private reportClaimFnolService: ReportClaimFnolService,
    private store: Store<fromRoot.RootState>,
    private analyticsFacade: AnalyticsFacade,
    private copyService: CopyService,
    private spinner: LoadingSpinnerService
  ) {}

  ngOnInit() {
    this.spinner.start({
      blockActions: true
    });
    this.amfamDotCom = env.links.amfamDotComUrl;
    this.lossCauses = this.copyService.get('claims', 'lossCauses');
    this.collisionDetailCauses = this.copyService.get('claims', 'collisionDetailCauses');
    this.weatherDetailCauses = this.copyService.get('claims', 'weatherDetailCauses');
    this.theftDetailCauses = this.copyService.get('claims', 'theftDetailCauses');
    this.buildForm();

    this.loading = this.store.select(claimFnolQuery.getSelectedEntityLoading);

    // Fetch saved draft claim data from report-claim-risk-selection.comp.
    combineLatest(
      this.store.select(claimFnolQuery.getClaimFnolSelectedEntity),
      this.store.select(PolicySelectors.selectClaimableRiskModels),
      this.store.select(PolicySelectors.selectPolicies),
      (draftClaim, risks, policies) => {
        return {
          draftClaim: draftClaim,
          risks: risks,
          policies: policies
        };
      }
    )
      .pipe(
        takeUntil(this.stop$),
        filter(state => {
          return state && !_some([state], { loading: true });
        })
      )
      .subscribe(state => {
        this.risks = state.risks;
        this.draftClaim = state.draftClaim;
        this.claimNumber = this.draftClaim.claimNumber;
        this.currentRisk = this.risks.find(
          risk => risk.vin === _get(this.draftClaim, 'autoClaimDetail.vehicles[0].vin', '')
        );
        this.spinner.stop();
        if (this.causeGroup.valid) {
          /**
           * AS: If we have gotten a response from the api call after save & continue
           * we need to decide if we redirect to next screen or take the user to summary page.
           */
          if (_get(this.draftClaim, 'fnolDetail.whatHappened', undefined)) {
            /**
             * This mean we already have the information for the next screen, take the user to
             * the summary page then.
             */
            this.reportClaimFnolService.getRoutingPath(this.draftClaim);
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_CONTACT]
              })
            );
          }
        }
        if (state.policies) {
          this.reportClaimFnolService.setClaimsPageTitle(
            state.policies.filter(policy => policy.policyNumber === this.draftClaim.policyNumber)[0]
          );
        }
      });
    if (_get(this.currentRisk, 'vehicleType') !== VehicleType.Boat) {
      this.theftDetailCauses = this.theftDetailCauses.filter(
        item => item && !_get(item, 'isEligibleForVehicleTypeOfBoat')
      );
    }

    /**
     * Condition to check required data is available to populate on landing from edit-risk-summary or via back button.
     */
    if (!!this.draftClaim && this.draftClaim.fnolDetail.typeOfLossCauseCode) {
      this.getSavedClaimInfo();
    }

    this.analyticsFacade.trackPage(ClaimsAnalytics.pageAutoClaimLossCause);
  }

  // onDestroy emit value and complete
  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  buildForm() {
    this.lossCauseForm = this.formBuilder.group({
      cause: this.formBuilder.group({
        lossCause: ['', Validators.required],
        collisionStationary: [''],
        collisionLoss: [''],
        theftLoss: [''],
        weatherLoss: ['']
      })
    });

    this.causeGroup = <UntypedFormGroup>this.lossCauseForm.controls['cause'];
  }

  private getSavedClaimInfo() {
    const incident: Incident = this.reportClaimFnolService.getFormattedLossCause(
      this.draftClaim.fnolDetail.typeOfLossCauseCode
    );
    /**
     * Pre-select with known lossCause.
     */
    this.lossCause = _find(this.lossCauses, { name: incident.cause });
    if (_isEmpty(this.lossCause)) {
      this.unMappedLossCause = incident.cause;
    } else {
      this.causeGroup.controls[ClaimsFnolFormControls.LOSS_CAUSE].setValue(this.lossCause.value);
      switch (this.lossCause.value) {
        case 'COLLISION':
          const collisionCause = _find(this.collisionDetailCauses, { name: incident.detail });
          const collisionDetail = collisionCause ? collisionCause.value : '';
          const multipleMovingVehiclesIndicator: string =
            String(_get(this.draftClaim, 'fnolDetail.multipleMovingVehiclesIndicator', '')) ===
            'true'
              ? 'yes'
              : 'no';
          this.causeGroup.controls[ClaimsFnolFormControls.COLLISION_STATIONARY].setValue(
            multipleMovingVehiclesIndicator
          );
          this.causeGroup.controls[ClaimsFnolFormControls.COLLISION_LOSS].setValue(collisionDetail);
          break;
        case 'WEATHER':
          const weatherCause = _find(this.weatherDetailCauses, { name: incident.detail });
          const weatherDetail = weatherCause ? weatherCause.value : '';
          this.causeGroup.controls[ClaimsFnolFormControls.WEATHER_LOSS].setValue(weatherDetail);
          break;
        case 'THEFT':
          const theftCause = _find(this.theftDetailCauses, { name: incident.detail });
          const theftDetail = theftCause ? theftCause.value : '';
          this.causeGroup.controls[ClaimsFnolFormControls.THEFT_VANDALISM].setValue(theftDetail);
          break;
        default:
          break;
      }
    }
  }

  resetControlValueAndValidators(formControlName) {
    const causeControl = this.causeGroup.controls;
    causeControl[formControlName].setValidators(null);
    causeControl[formControlName].setValue('');
    causeControl[formControlName].markAsPristine();
    causeControl[formControlName].markAsUntouched();
  }

  changeLossCause(value: any) {
    if (value) {
      this.lossCause = Object.assign({}, { value: value });
      switch (this.lossCause.value) {
        case 'COLLISION':
          this.causeGroup.controls[ClaimsFnolFormControls.COLLISION_STATIONARY].setValidators(
            Validators.compose([Validators.required])
          );
          this.causeGroup.controls[ClaimsFnolFormControls.COLLISION_LOSS].setValidators(
            Validators.compose([Validators.required])
          );
          this.resetControlValueAndValidators(ClaimsFnolFormControls.THEFT_VANDALISM);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.WEATHER_LOSS);
          break;
        case 'ROADSIDE ASSISTANCE REIMBURSEMENT':
        case 'OTHER':
        case 'FIRE':
          this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_STATIONARY);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_LOSS);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.THEFT_VANDALISM);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.WEATHER_LOSS);
          break;
        case 'THEFT':
          this.causeGroup.controls[ClaimsFnolFormControls.THEFT_VANDALISM].setValidators(
            Validators.compose([Validators.required])
          );

          this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_STATIONARY);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_LOSS);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.WEATHER_LOSS);
          break;
        case 'WEATHER':
          this.causeGroup.controls[ClaimsFnolFormControls.WEATHER_LOSS].setValidators(
            Validators.compose([Validators.required])
          );

          this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_STATIONARY);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_LOSS);
          this.resetControlValueAndValidators(ClaimsFnolFormControls.THEFT_VANDALISM);
          break;
      }
    } else {
      this.lossCause.value = '';

      this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_STATIONARY);
      this.resetControlValueAndValidators(ClaimsFnolFormControls.COLLISION_LOSS);
      this.resetControlValueAndValidators(ClaimsFnolFormControls.THEFT_VANDALISM);
      this.resetControlValueAndValidators(ClaimsFnolFormControls.WEATHER_LOSS);
    }
    this.causeGroup.updateValueAndValidity();
  }

  goBackOneStep() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_INCIDENT_LOCATION_PATH]
      })
    );
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_HOME_PATH]
      })
    );
  }
  /**
   * 1. Check claimNumber exists on draftClaim node level.
   *   1.2.1 Available - proceed with update FNOL operation
   *   1.2.2 Un Available - Do create FNOL operation and on ..
   *    1.2.2.1 Success - proceed with update FNOL operation
   *    1.2.2.2 Fail - Redirect to Contact component(because update operation needs claimNumber).
   */
  submitLossCause(): void {
    const updateClaimRequestObj =
      this.reportClaimFnolService.createUpdateDraftClaimLossCauseFactory(
        this.causeGroup.value,
        this.unMappedLossCause,
        this.draftClaim
      );
    this.spinner.start({
      blockActions: true
    });
    if (!this.claimNumber) {
      const draftClaim: DraftClaim = Object.assign({}, this.draftClaim, updateClaimRequestObj.fnol);
      this.store.dispatch(new fromClaimFnolActions.CreateDraftClaim(draftClaim));
    } else {
      this.store.dispatch(new fromClaimFnolActions.UpdateDraftClaim(updateClaimRequestObj));
    }
  }
  submitUnMappedLossCause(): void {
    this.submitLossCause();
  }
}
